<!-- This component is the Confirmation step where the user will input the information about the Proposed Insured and Policy Owner. -->
<template>
	<div class="flex grow flex-col gap-8 px-6 py-10">
		<section class="flex flex-col gap-4 border-b border-slate-200 pb-4">
			<h1 class="text-2xl font-bold">{{ t('document.proposedInsured') }}</h1>
			<IdentityForm
				v-model="proposedInsured"
				:key="`proposedInsured-${proposedInsured.id}`"
				:disabled="sessionDocumentsStore.currentStep?.completed"
				id="proposedInsured"
				@validation="updateValidation('proposedInsured', $event)"
				:truncate="planEncrypted"
			/>

			<ConfirmIdentity
				:plan="proposedInsured"
				v-if="planEncrypted"
			/>

			<div
				class="grid grid-cols-3 gap-4 *:w-full *:max-w-none"
				v-else
			>
				<ProducerNumberDropdown />
				<div class="flex flex-col items-end gap-2">
					<FormKit
						v-model="proposedInsured.ownerIsInsured"
						:label="t('document.ownerIsInsured')"
						:disabled="sessionDocumentsStore.currentStep?.completed"
						type="checkbox"
						decorator-icon="StopIcon"
						decorator-icon-class="max-w-none"
						decorator-class="border-gray-400!"
						outer-class="inline-flex mb-0!"
						name="ownerIsInsured"
					/>
				</div>
			</div>
		</section>

		<template v-if="!planEncrypted">
			<section
				v-if="!proposedInsured.ownerIsInsured"
				class="flex flex-col gap-4 border-b border-slate-200 pb-4"
			>
				<h1 class="text-2xl font-bold">
					{{ t('document.policyOwnerInformation') }}
				</h1>
				<IdentityForm
					v-model="policyOwner"
					:key="`policyOwner-${proposedInsured.id}`"
					:disabled="sessionDocumentsStore.currentStep?.completed"
					id="policyOwner"
					@validation="updateValidation('policyOwner', $event)"
				/>
			</section>
			<div :class="`grid grid-cols-2 gap-4 *:w-full *:max-w-none`">
				<FormKit
					v-model="proposedInsured.beneficiary"
					:label="t('document.beneficiary')"
					:disabled="sessionDocumentsStore.currentStep?.completed"
					name="beneficiary"
					type="text"
				/>
				<FormKit
					v-model="proposedInsured.beneficiaryRelationship"
					:label="t('document.beneficiaryRelationship')"
					:disabled="sessionDocumentsStore.currentStep?.completed"
					name="beneficiaryRelationship"
					type="text"
				/>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import IdentityForm from './IdentityForm.vue'
import ProducerNumberDropdown from './ProducerNumberDropdown.vue'
import Plan from '@/classes/Plan'
import ConfirmIdentity from '@/components/encryption/ConfirmIdentityInline.vue'
import { usePresentationLocale } from '@/i18n'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import { computed, ref, watch } from 'vue'

const { t } = usePresentationLocale()

const sessionStore = useSessionStore()
const sessionDocumentsStore = useSessionDocumentsStore()
const proposedInsured = computed({
	get: () =>
		sessionStore.getPlanHolder(
			sessionDocumentsStore.currentPlanHolderID
		) as Plan,
	set: (value) => proposedInsured.value.update(value),
})

// The policyOwner's value is depended on whether the owner is insured. If true, the policy owner will copy the proposedInsured value.
const policyOwner = computed({
	get: () =>
		proposedInsured.value.ownerIsInsured
			? proposedInsured.value
			: proposedInsured.value.policyOwner,
	set: (value) => (proposedInsured.value.policyOwner = value),
})

const planEncrypted = computed(
	() => proposedInsured.value.encryptionStatus !== 'unencrypted'
)

// Validation is emitted from the IdentityForm.
const validation = ref({ proposedInsured: false, policyOwner: false })
const updateValidation = (
	section: 'proposedInsured' | 'policyOwner',
	valid: boolean
) => {
	validation.value[section] = valid
	storeValidation()
}

// The policy owner form is unmounted while the owner is insured, so its last
// emitted validity goes stale. Re-evaluate the step whenever the box is toggled.
watch(() => proposedInsured.value.ownerIsInsured, storeValidation)

// This sets the step to "validated".
// If the owner is insured, the policy owner is the proposedInsured, so only that form counts.
function storeValidation() {
	if (sessionDocumentsStore.currentStep?.label !== 'confirmation') return

	sessionDocumentsStore.setStepValidated(
		sessionDocumentsStore.currentStep,
		validation.value.proposedInsured &&
			(proposedInsured.value.ownerIsInsured || validation.value.policyOwner)
	)
}
</script>

<style scoped>
@reference "tailwindcss";
:deep([data-checked='true'] .formkit-icon) {
	@apply text-green-400;
}
</style>
